import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, Flex, Switch, Text } from '@chakra-ui/react'
import { AccessorySelectionType, GetPriceResponse } from '@leid/api-orders'
import { ProductUsage } from '@leid/api-products'
import { usePathname } from 'next/navigation'

import { trackClickAccessoryButton } from 'app/components/shared/Analytics/segment'
import { CustomerInfo } from 'app/utils/authentication'
import { ItemApi } from 'appTypes/items'
import { OrderItemInfo } from 'appTypes/orders'
import { RectButton } from 'components/ui/Buttons/RectButton'
import { ProductCardHorizontal } from 'components/ui/Card/ProductCardHorizontal'
import { InteractiveBox } from 'components/ui/InteractiveBox'
import apiClient from 'helpers/clients/apiClient'
import { getAvailabilitiesChanges } from 'helpers/utils/availabilities'
import { selectOrderState } from 'store/partials/cart/cartSelectors'

import { ComplementaryItemInfo } from './models'

type Props = {
  accessoryItems: ItemApi[] | undefined
  fromDate: Date
  toDate: Date
  selectedLocationId: number
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void
  isEditMode: boolean
  orderItem: OrderItemInfo | undefined
  setIsRerent: (isRerent: boolean) => void
  includeInsurance: boolean | undefined
  includeDeductibles: boolean | undefined
  customerInfo: CustomerInfo
}

const AccessoryItems = (props: Props): JSX.Element | null => {
  const {
    accessoryItems,
    setFieldValue,
    fromDate,
    toDate,
    selectedLocationId,
    setIsRerent,
    includeInsurance,
    includeDeductibles,
    customerInfo,
    orderItem,
    isEditMode,
  } = props

  const pathname = usePathname()
  const { t } = useTranslation('common')
  const { cart } = useSelector(selectOrderState)

  const [accessoriesToShow, setAccessoriesToShow] = useState<ItemApi[]>([])
  const [disableAmountButtons, setDisableAmountButtons] = useState(false)
  const [quantity, setQuantity] = useState<Record<number, number>>({})
  const [availableAmounts, setAvailableAmounts] = useState<Record<number, ComplementaryItemInfo>>({})
  const [prices, setPrices] = useState<Record<number, GetPriceResponse>>({})

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoryItems])

  useEffect(() => {
    getPrices(accessoriesToShow)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInsurance, includeDeductibles])

  const loadData = async () => {
    if (!accessoryItems || accessoryItems.length === 0) {
      return
    }

    let initialQuantity: Record<number, number> = {}
    let availableAmounts: Record<number, ComplementaryItemInfo> = {}
    const accessoriesToShow: ItemApi[] = []

    const orderItemId = orderItem?.orderItem?.id

    for (const item of accessoryItems) {
      const key = item.id
      let amountOfAccessories = 0

      if (isEditMode && orderItemId && cart) {
        const existingAccessories = cart.orderItems.filter(
          (s) =>
            s.orderItem.associatedParentOrderItemId === orderItemId &&
            s.orderItem.productId === item.id &&
            s.orderItem.locationId === selectedLocationId,
        )
        amountOfAccessories = existingAccessories.reduce((acc, curr) => acc + curr.orderItem.amount, 0)
      }

      const amountInfo = await getProductAmountInfo(item.id, orderItemId)
      if (!amountInfo) {
        // No amount info - skip this item
        initialQuantity = {
          ...initialQuantity,
          [key]: amountOfAccessories,
        }
        continue
      }

      availableAmounts = {
        ...availableAmounts,
        [key]: amountInfo,
      }

      if (amountInfo.selectionType === AccessorySelectionType.MANDATORY && amountInfo.amount === 0) {
        setIsRerent(true)
        return
      }

      if (
        (amountInfo.selectionType === AccessorySelectionType.MANDATORY ||
          amountInfo.selectionType === AccessorySelectionType.PRE_SELECTED) &&
        amountOfAccessories === 0 &&
        amountInfo.amount !== 0
      ) {
        amountOfAccessories = 1
      }

      initialQuantity = {
        ...initialQuantity,
        [key]: amountOfAccessories,
      }

      accessoriesToShow.push(item)
    }

    await getPrices(accessoriesToShow)

    setQuantity(initialQuantity)
    setAvailableAmounts(availableAmounts)
    setAccessoriesToShow(accessoriesToShow)

    if (setFieldValue) {
      setFieldValue('complementaryItemsQuantity', initialQuantity)
    }
  }

  const getPrices = async (accessoriesToShow: ItemApi[]) => {
    if (accessoriesToShow.length === 0) {
      setPrices({})
      return
    }

    let prices: Record<number, GetPriceResponse> = {}

    for (const item of accessoriesToShow) {
      const key = item.id

      const priceResponse = await apiClient.getProductPrice(item.id, {
        rentedFrom: fromDate,
        rentedTo: toDate,
        locationId: selectedLocationId,
        includeCleaning: false,
        includeInsurance: includeInsurance ?? false,
        includeDeductibles: includeDeductibles ?? false,
        organizationId: customerInfo.organization?.id,
        amount: 1,
      })
      if (priceResponse.data) {
        prices = { ...prices, [key]: priceResponse.data }
      }
    }

    setPrices(prices)
  }

  const getProductAmountInfo = async (
    productId: number,
    orderItemId: number | undefined,
  ): Promise<ComplementaryItemInfo | undefined> => {
    const response = await apiClient.getProductCalendarList(productId, {
      startDate: fromDate,
    })

    if (!response.success || !response.data) {
      return { amount: 0, selectionType: undefined }
    }

    const availabilityData = response.data.locations.find((s) => s.locationId === selectedLocationId)
    if (!availabilityData) {
      // No asset available at the location - HIDE accessory
      return undefined
    }

    const changes = getAvailabilitiesChanges(fromDate, toDate, availabilityData, true)

    let availableAmount = 0
    let existingAmount = 0
    if (availabilityData.hasConsumableAssets) {
      availableAmount = Math.min(...changes)

      if (orderItemId) {
        existingAmount =
          cart?.orderItems.find(
            (item) =>
              item.orderItem.associatedParentOrderItemId === orderItemId &&
              item.orderItem.productId === productId &&
              item.orderItem.locationId === selectedLocationId,
          )?.orderItem.amount ?? 0
      }
    } else {
      if (changes.some((s) => s === 0)) {
        availableAmount = 0
      } else {
        availableAmount = Math.min(...changes)
      }

      if (orderItemId) {
        existingAmount =
          cart?.orderItems.filter(
            (item) =>
              item.orderItem.associatedParentOrderItemId === orderItemId &&
              item.orderItem.productId === productId &&
              item.orderItem.locationId === selectedLocationId,
          )?.length ?? 0
      }
    }

    const amount = availableAmount + existingAmount
    return { amount: amount, selectionType: availabilityData.accessorySelectionType }
  }

  const changeQuantity = async (id: number, value: number) => {
    setDisableAmountButtons(true)

    const orderItemId = orderItem?.orderItem?.id

    const amountInfo = await getProductAmountInfo(id, orderItemId)
    if (!amountInfo) {
      return
    }

    const newQuantity = {
      ...quantity,
      [id]: quantity[id] + value,
    }

    const newAvailableAmounts = {
      ...availableAmounts,
      [id]: amountInfo,
    }
    setAvailableAmounts(newAvailableAmounts)

    setQuantity(newQuantity)

    if (setFieldValue) {
      setFieldValue('complementaryItemsQuantity', newQuantity)
    }

    const accessory = accessoriesToShow.find((s) => s.id === id)
    trackClickAccessoryButton(accessory?.name ?? '', newQuantity[id], pathname ?? '')

    setDisableAmountButtons(false)
  }

  const selectService = (id: number, value: boolean) => {
    setDisableAmountButtons(true)

    const newQuantity = {
      ...quantity,
      [id]: value ? 1 : 0,
    }
    setQuantity(newQuantity)

    if (setFieldValue) {
      setFieldValue('complementaryItemsQuantity', newQuantity)
      setFieldValue('hasServices', value)
    }

    setDisableAmountButtons(false)
  }

  const isRemoveButtonDisabled = (itemId: number): boolean => {
    if (disableAmountButtons) {
      return true
    }

    const currentQuantity = quantity[itemId]
    const amountsInfo = availableAmounts[itemId]
    if (amountsInfo && amountsInfo.selectionType === AccessorySelectionType.MANDATORY && currentQuantity === 1) {
      return true
    }

    if (currentQuantity === 0) {
      return true
    }

    return false
  }

  const isAddButtonDisabled = (itemId: number): boolean => {
    if (disableAmountButtons) {
      return true
    }

    const amountsInfo = availableAmounts[itemId]
    if (amountsInfo.amount === 0) {
      return true
    }

    const currentQuantity = quantity[itemId]
    if (currentQuantity === amountsInfo.amount) {
      return true
    }

    return false
  }

  const isSwitchDisabled = (): boolean => {
    if (disableAmountButtons) {
      return true
    }

    return false
  }

  if (!accessoriesToShow || accessoriesToShow.length === 0) {
    return null
  }

  return (
    <InteractiveBox>
      <Text fontSize="md">{t('accessories')}</Text>
      {accessoriesToShow?.map((item, index) => (
        <Flex
          key={index}
          marginTop="20px"
          justifyContent="space-between"
          width="100%"
          className="gtm-complementary-item-wrapper"
        >
          <Box width="80%">
            <ProductCardHorizontal
              product={item.products[0]}
              isDark
              price={item.price}
              pricePerDay={item.pricePerDay}
              calculatedPrice={prices[item.id]}
              amount={quantity[item.id]}
            />
          </Box>

          {item.productUsage === ProductUsage.SERVICE ? (
            <Box width="20%" display="flex" justifyContent="center" alignItems="center">
              <Box width="100%" display="flex" justifyContent="center">
                <Switch.Root
                  size="lg"
                  colorPalette="leidTheme"
                  onCheckedChange={(e) => selectService(item.id, e.checked)}
                  checked={quantity[item.id] !== 0}
                  defaultChecked={quantity[item.id] !== 0}
                  disabled={isSwitchDisabled()}
                >
                  <Switch.HiddenInput />
                  <Switch.Control />
                </Switch.Root>
              </Box>
            </Box>
          ) : (
            <Box width="20%">
              <Flex height="24px" alignItems="center" className="gtm-change-quantity-wrapper">
                <RectButton
                  isDisabled={isRemoveButtonDisabled(item.id)}
                  onClick={() => changeQuantity(item.id, -1)}
                  type="button"
                  aria-label="Remove complementary item"
                >
                  <img src="/icons/minus.svg" alt="" />
                </RectButton>
                <Text
                  marginX="8px"
                  width="17px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  color="black"
                  fontSize="xs"
                  fontWeight={600}
                >
                  {quantity[item.id]}
                </Text>
                <RectButton
                  onClick={() => changeQuantity(item.id, 1)}
                  isDisabled={isAddButtonDisabled(item.id)}
                  type="button"
                  aria-label="Add complementary item"
                >
                  <img src="/icons/plus.svg" alt="" />
                </RectButton>
              </Flex>
              {availableAmounts[item.id]?.selectionType === AccessorySelectionType.MANDATORY && (
                <Text
                  color="green.a200"
                  fontSize="xs"
                  fontWeight={600}
                  lineHeight="12px"
                  marginBottom="3px"
                  marginTop="5px"
                  textAlign="center"
                >
                  {t('mandatoryAccessoryText')}
                </Text>
              )}
            </Box>
          )}
        </Flex>
      ))}
    </InteractiveBox>
  )
}

export default AccessoryItems
